import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { Upload, CheckCircle, AlertCircle, Users, Package, Loader2, Download } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const UploadBlock = ({ title, icon: Icon, endpoint, expectedSize, helpText, dataTestid }) => {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const upload = () => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setResult(null);
    setError(null);

    const form = new FormData();
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}${endpoint}`, true);
    xhr.withCredentials = true;
    xhr.timeout = 15 * 60 * 1000; // 15 minutes

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      setUploading(false);
      let body = null;
      try { body = JSON.parse(xhr.responseText); } catch { /* ignore */ }
      if (xhr.status >= 200 && xhr.status < 300) {
        setResult(body || { ok: true });
      } else if (xhr.status === 504) {
        // Proxy timeout but the import most likely completed in background
        setError("Le proxy a renvoyé 504 (timeout), mais l'import continue en arrière-plan. Rechargez la page dans 1 min, ou vérifiez via le statut.");
      } else {
        setError((body && body.detail) || `HTTP ${xhr.status}`);
      }
    };
    xhr.onerror = () => {
      setUploading(false);
      setError('Erreur réseau pendant l’upload.');
    };
    xhr.ontimeout = () => {
      setUploading(false);
      setError('Timeout — relancez ou vérifiez le statut.');
    };

    xhr.send(form);
  };

  return (
    <Card className="border border-border rounded-none" data-testid={dataTestid}>
      <CardHeader className="flex flex-row items-center gap-2">
        <Icon className="w-4 h-4 text-primary" />
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{helpText}</p>

        <div className="flex items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setError(null); }}
            data-testid={`${dataTestid}-input`}
          />
          <Button type="button" variant="outline" className="rounded-none" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`${dataTestid}-choose`}>
            <Upload className="w-4 h-4 mr-2" /> Choisir un fichier .json
          </Button>
          {file && (
            <div className="text-sm">
              <span className="font-medium">{file.name}</span>{' '}
              <span className="text-muted-foreground">({(file.size / (1024 * 1024)).toFixed(1)} MB)</span>
            </div>
          )}
        </div>

        {expectedSize && !file && (
          <p className="text-[11px] italic text-muted-foreground">Taille attendue : {expectedSize}</p>
        )}

        {file && !uploading && !result && (
          <Button onClick={upload} className="rounded-none" data-testid={`${dataTestid}-start`}>
            <Upload className="w-4 h-4 mr-2" /> Démarrer l'import
          </Button>
        )}

        {uploading && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <span>Upload {progress}%… puis traitement serveur (peut prendre quelques minutes)</span>
            </div>
            <div className="w-full bg-gray-200 h-1.5 rounded-none overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {result && (
          <div className="bg-green-50 border border-green-200 p-4 space-y-1" data-testid={`${dataTestid}-result`}>
            <div className="flex items-center gap-2 text-green-800 font-medium">
              <CheckCircle className="w-4 h-4" /> Import terminé
            </div>
            <pre className="text-xs text-green-900 mt-2 whitespace-pre-wrap font-mono">{JSON.stringify(result, null, 2)}</pre>
          </div>
        )}

        {error && (
          <div className="bg-amber-50 border border-amber-300 p-4">
            <div className="flex items-start gap-2 text-amber-900">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const formatBytes = (n) => {
  if (!n) return '–';
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
};

const DownloadsBlock = () => {
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/wp-import/download-list`, { credentials: 'include' });
        if (res.ok) setFiles((await res.json()).files || []);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  return (
    <Card className="border border-border rounded-none" data-testid="wp-import-downloads">
      <CardHeader className="flex flex-row items-center gap-2">
        <Download className="w-4 h-4 text-primary" />
        <CardTitle className="text-base">Télécharger les dumps depuis ce serveur</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Les dumps WordPress sont stockés dans <code className="text-xs bg-gray-100 px-1">backend/data/</code> sur ce serveur.
          Téléchargez-les ici, puis ré-uploadez-les sur l'environnement cible (staging / prod) via les blocs ci-dessous.
        </p>
        {loading ? (
          <p className="text-sm text-muted-foreground">Chargement…</p>
        ) : (
          <div className="space-y-2">
            {(files || []).map((f) => (
              <div key={f.name} className="flex items-center justify-between p-3 border border-border bg-gray-50/50">
                <div>
                  <div className="font-mono text-sm">{f.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {f.available ? formatBytes(f.size_bytes) : 'Indisponible sur ce serveur'}
                  </div>
                </div>
                <a
                  href={`${API_BASE}/admin/wp-import/download/${f.name}`}
                  download={f.name}
                  className={`inline-flex items-center gap-2 text-sm font-medium px-3 py-1.5 ${f.available ? 'text-primary hover:underline' : 'text-muted-foreground pointer-events-none opacity-50'}`}
                  data-testid={`download-${f.name}`}
                >
                  <Download className="w-3.5 h-3.5" /> Télécharger
                </a>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const WpImportPanel = () => {
  return (
    <div className="space-y-6" data-testid="wp-import-panel">
      <Card className="border border-blue-200 bg-blue-50/40 rounded-none">
        <CardContent className="p-6 space-y-2">
          <h3 className="font-semibold text-blue-900">Migration WordPress — uploads</h3>
          <p className="text-sm text-blue-800">
            Faites les imports <strong>dans l'ordre</strong>:
          </p>
          <ol className="text-sm text-blue-900 list-decimal ml-5 space-y-1">
            <li>D'abord <strong>les utilisateurs</strong> (corrige les emails inversés).</li>
            <li>Ensuite <strong>les commandes historiques</strong> (matchage par user_id puis email).</li>
          </ol>
          <p className="text-xs text-blue-800 italic mt-2">Les imports sont <strong>idempotents</strong> : vous pouvez les relancer sans risque, les mots de passe déjà mis à niveau (bcrypt) sont préservés.</p>
        </CardContent>
      </Card>

      <DownloadsBlock />

      <UploadBlock
        title="1. Utilisateurs WordPress (wp_users_dump.json)"
        icon={Users}
        endpoint="/admin/wp-import/users"
        expectedSize="5 MB · ~2528 users"
        helpText="Importe / met à jour les comptes clients migrés depuis WordPress, avec leur hash phpass. La nouvelle priorité user_email > billing_email corrige automatiquement les ~62 comptes mal-emailés (compteur 'relocated_email')."
        dataTestid="upload-users"
      />

      <UploadBlock
        title="2. Commandes historiques WooCommerce (wp_orders_dump.json)"
        icon={Package}
        endpoint="/admin/wp-import/orders"
        expectedSize="~140 MB · ~13 399 commandes"
        helpText="Importe les commandes historiques dans la collection 'legacy_quotes'. Affichées en lecture seule dans l'espace client avec badge 'HISTORIQUE WP'. Match par wp_user_id puis fallback par email de facturation."
        dataTestid="upload-orders"
      />

      <Separator />
      <p className="text-[11px] text-muted-foreground">
        Astuce : si vous obtenez une erreur "504 Gateway Timeout" sur l'upload des commandes (142 MB),
        c'est seulement le proxy qui a coupé — le serveur termine l'import en arrière-plan.
        Rechargez après 1-2 min pour voir le résultat.
      </p>
    </div>
  );
};

export default WpImportPanel;
